import React from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box, Flex, Inline, Stack } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { MOCK_FEATURE_LIST } from '../../../constants/mockData';
import { useAudioPlayerStore } from '../../hooks/useAudioPlayerStore';
import { Playlist } from '../playlist/Playlist';

import { AudioFeedbackFooter } from './AudioFeedbackFooter';
import { AudioHeaderPanel } from './AudioHeaderPanel';
import { AudioPlayerError } from './AudioPlayerError';
import { AudioTimeIndicator } from './AudioTimeIndicator';
import { MiniplayerControls } from './controls/MiniplayerControls';

const i18n = defineMessages({
	closeButtonLabel: {
		id: 'audio.audio-player.close-player-button',
		defaultMessage: 'Close audio player',
		description: 'The label used for the X icon to close the audio player',
	},
});

const styles = cssMap({
	expandedAudioPlayerContainer: {
		width: '380px',
		minHeight: '470px',
		position: 'relative',
		paddingTop: token('space.150'),
		paddingRight: token('space.150'),
		paddingBottom: token('space.150'),
		paddingLeft: token('space.150'),
	},
	buttonsContainer: {
		paddingInline: token('space.050'),
	},
});

// Error is a temporary prop - remove once implemented in the audio player store
export const ExpandedAudioPlayer = ({
	error,
	audioRef,
	fetchAudio,
}: {
	error?: Error;
	audioRef: React.RefObject<HTMLAudioElement>;
	fetchAudio: () => void;
}) => {
	const { formatMessage } = useIntl();

	const { closeAudioPlayer } = useAudioPlayerStore();

	return (
		<Box
			xcss={styles.expandedAudioPlayerContainer}
			style={{
				background: `linear-gradient(180deg, ${token('elevation.surface')} 50%, ${token('elevation.surface.sunken')} 100%)`,
			}}
		>
			<Stack space="space.250" alignInline="center">
				<Inline alignInline="end" alignBlock="center" xcss={styles.buttonsContainer} grow="fill">
					<IconButton
						appearance="subtle"
						spacing="compact"
						icon={CrossIcon}
						label={formatMessage(i18n.closeButtonLabel)}
						onClick={closeAudioPlayer}
					/>
				</Inline>
				<AudioHeaderPanel featureList={MOCK_FEATURE_LIST} />

				{error ? (
					<Flex justifyContent="center">
						<AudioPlayerError onRetry={() => {}} />
					</Flex>
				) : (
					<>
						<MiniplayerControls audioRef={audioRef} fetchAudio={fetchAudio} />
						<AudioTimeIndicator audioRef={audioRef} />
					</>
				)}
				<AudioFeedbackFooter />
			</Stack>
			<Playlist />
		</Box>
	);
};
